import {
  AutoComplete,
  ReadOnly,
  useEntityPermission,
  useDataProvider,
  useMasterData,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { useEffect, useState, forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';

/*
The States selector alwaays needs a country.
There are 2 ways to provide country
  1) Either you allow a country selector, so you can choose the country and then select state for that country
    - Add attribute  "dependencies": {"countryId": "customerAddressCountry"} to teh schema for State object
    - e.g 
    {
      "row": 1,
      "name": "customerAddressState",
      "label": "State",
      "control": "States",
      "dependencies": {
          "countryId": "customerAddressCountry"
      }
    }
 2) You can add a static country to 
      AppDataProvider => getInjectableAdditionalMetaData to return masterData?.frontendAppData?.staticCountry
      which is then used in the State Selector logic
*
*/

const StatesSelector = forwardRef(
  ({ overwriteProps = {}, field, onChange, ...props }, ref) => {
    const nameKey = 'name';
    const valueKey = 'id';

    const masterData = useMasterData();

    const staticCountry = masterData?.frontendAppData?.staticCountry;

    const provider =
      process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
    const { get, getApiUrl } = useDataProvider(provider);
    const [defaultOptions, setDefaultOptions] = useState([]);

    const { setValue, getValues, watch } = useFormContext();

    const currentState = getValues(field?.name);

    const currentCountry = staticCountry?.value
      ? staticCountry
      : watch(field?.dependencies?.countryId || 'undefined');

    const getStates = async (countryIdArg, stateSearchValue = '') => {
      const searchParamm = stateSearchValue ? `&name=${stateSearchValue}` : '';
      const result = await get(
        `${getApiUrl()}/sxcl/v1/states?limit=100&countryId=${countryIdArg}${searchParamm}`,
      );
      if (result.ok) {
        const stateResults =
          result?.data?.data?.map(item => ({
            label: item[nameKey],
            value: item[valueKey],
          })) || [];
        return stateResults;
      }
      return null;
    };

    const loadOptions = async (searchValue, callback) => {
      if (!searchValue) return [];
      const resultsData = await getStates(currentCountry?.value, searchValue);
      if (resultsData) {
        return callback(resultsData);
      }
      return callback([]);
    };

    const initialLoad = async currentCountryIdArg => {
      const result = await getStates(currentCountryIdArg);
      if (currentState?.value) {
        setDefaultOptions([
          currentState,
          ...result.filter(item => item.value !== currentState?.value),
        ]);
      } else {
        setDefaultOptions(result);
      }
    };

    useEffect(() => {
      if (currentCountry?.value) {
        initialLoad(currentCountry?.value);
      } else {
        setDefaultOptions([]);
        if (currentState?.value) {
          setValue(field.name, { label: '', value: '' });
        }
      }
    }, [JSON.stringify(currentCountry)]);

    const { isHidden, isReadOnly } = useEntityPermission();

    if (isReadOnly(props?.permission, currentState)) {
      return <ReadOnly name={props?.name}>{currentState?.label}</ReadOnly>;
    }

    if (isHidden(props?.permission)) {
      return (
        <input
          type="hidden"
          name={props?.name}
          value={currentState?.value || ''}
        />
      );
    }

    return (
      <AutoComplete
        ref={ref}
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        {...props}
        overwriteProps={overwriteProps}
        isClearable
      />
    );
  },
);

export default observer(StatesSelector);
